// Phase 14 — Admin Affiliate Billing Controls
// ============================================
// Lets admins toggle payment_required / billing_enabled / access_type per
// affiliate, see Stripe subscription state, and manage migration rows.
import React, { useEffect, useState, useCallback } from "react";
import {
  RefreshCw, Search, DollarSign, ShieldCheck, Pause, Play, X, CreditCard,
  Loader2, AlertTriangle, History,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const ACCESS_TYPES = ["paid", "free_entry", "comped", "earned_tier", "manual_override", "migrated"];
const TIERS = ["lift_off", "orbit", "galaxy", "deep_cosmos", "cosmos"];

const STATUS_TONE = {
  paid:         "text-lifted-green",
  free_access:  "text-amber-300",
  past_due:     "text-rose-300",
  canceled:     "text-white/60",
  trialing:     "text-sky-300",
  comped:       "text-amber-300",
  migrated:     "text-sky-300",
  unknown:      "text-white/60",
  paused:       "text-amber-300",
};

function money(c) { if (c == null) return "—"; return `$${(c/100).toFixed(2)}`; }

export default function AdminAffiliateBilling() {
  const [tab, setTab] = useState("attention"); // attention | migration | tiers | uppromote
  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="admin-affiliate-billing">
      <header>
        <div className="eyebrow">Admin · Affiliates</div>
        <h1 className="font-anton text-3xl uppercase">Billing Controls</h1>
        <p className="text-xs text-lifted-dim mt-1 max-w-2xl">
          Admin can mark any affiliate paid, free, comped, or migrated.
          Turning billing off does <strong className="text-white">not</strong> deactivate
          the affiliate — access status and payment status are independent.
        </p>
      </header>
      <div className="flex gap-1 flex-wrap" data-testid="billing-tabs">
        {[
          ["attention", "Needs attention"],
          ["migration", "Migration review"],
          ["tiers", "Tier rules"],
          ["uppromote", "UpPromote import"],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
                  className={`px-3 py-1.5 rounded-full border text-xs font-bebas tracking-[0.18em] uppercase ${
                    tab === k ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40" : "border-white/10 text-lifted-dim"
                  }`} data-testid={`billing-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "attention" && <NeedsAttentionTab />}
      {tab === "migration" && <MigrationTab />}
      {tab === "tiers" && <TierRulesTab />}
      {tab === "uppromote" && <UpPromoteImportTab />}
    </div>
  );
}

// ---------------- Needs Attention -------------------------------------------
function NeedsAttentionTab() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/affiliates/billing-needs-attention");
      setRows(data.items || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="space-y-3" data-testid="billing-attention">
      {err && <div className="text-rose-300 text-sm">{err}</div>}
      {rows.length === 0 ? (
        <div className="glass-panel p-6 text-center text-xs text-lifted-dim">
          <ShieldCheck className="h-6 w-6 mx-auto text-lifted-green mb-2" />
          All affiliate billing is in good standing. No action required.
        </div>
      ) : (
        <BillingTable rows={rows} onEdit={setEditing} />
      )}
      {editing && <BillingEditor billing={editing} onClose={() => { setEditing(null); load(); }} />}
    </section>
  );
}

// ---------------- Migration tab ---------------------------------------------
function MigrationTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter) params.status = filter;
      const { data } = await api.get("/admin/affiliates/billing-migration", { params });
      setRows(data.items || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <section className="space-y-3" data-testid="billing-migration">
      <div className="flex flex-wrap gap-2">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs"
                data-testid="migration-status-filter">
          <option value="">All migrated</option>
          <option value="migrated">Migrated (Stripe attached)</option>
          <option value="needs_subscription">Needs subscription</option>
          <option value="needs_stripe_customer">Needs Stripe customer</option>
          <option value="manual_review">Manual review</option>
          <option value="free_access">Free access</option>
        </select>
        <button onClick={load} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="migration-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>
      {err && <div className="text-rose-300 text-sm">{err}</div>}
      {rows.length === 0 ? (
        <div className="glass-panel p-6 text-center text-xs text-lifted-dim">No migration rows match.</div>
      ) : (
        <BillingTable rows={rows} onEdit={setEditing} showMigration />
      )}
      {editing && <BillingEditor billing={editing} onClose={() => { setEditing(null); load(); }} />}
    </section>
  );
}

// ---------------- Tier rules tab --------------------------------------------
function TierRulesTab() {
  const [rules, setRules] = useState([]);
  const [busyTier, setBusyTier] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/affiliate-tier-billing-rules");
      setRules(data.items || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateRule = async (tier, patch) => {
    setBusyTier(tier); setErr("");
    try {
      await api.patch(`/admin/affiliate-tier-billing-rules/${tier}`, patch);
      await load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusyTier(""); }
  };

  return (
    <section className="space-y-3" data-testid="billing-tiers">
      {err && <div className="text-rose-300 text-sm">{err}</div>}
      <div className="glass-panel overflow-hidden">
        <table className="w-full text-xs">
          <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
            <tr className="border-b border-white/5">
              <th className="px-3 py-2 text-left">Tier</th>
              <th className="px-3 py-2 text-right">Monthly</th>
              <th className="px-3 py-2 text-center">Payment required</th>
              <th className="px-3 py-2 text-center">Free access allowed</th>
              <th className="px-3 py-2 text-center">Auto-waive on earn</th>
              <th className="px-3 py-2 text-center">Active</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.tier} className="border-b border-white/5" data-testid={`tier-row-${r.tier}`}>
                <td className="px-3 py-2 font-bebas tracking-[0.22em] uppercase">{r.tier.replace("_", " ")}</td>
                <td className="px-3 py-2 text-right">
                  <input type="number" value={(r.monthly_price_cents || 0) / 100}
                         onChange={(e) => updateRule(r.tier, { monthly_price_cents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                         disabled={busyTier === r.tier}
                         className="w-20 bg-black/40 border border-white/10 rounded px-1 py-0.5 text-right"
                         data-testid={`tier-${r.tier}-price`} />
                </td>
                <td className="px-3 py-2 text-center">
                  <SwitchCell value={r.default_payment_required} onChange={(v) => updateRule(r.tier, { default_payment_required: v })} testid={`tier-${r.tier}-payreq`} />
                </td>
                <td className="px-3 py-2 text-center">
                  <SwitchCell value={r.allow_free_access} onChange={(v) => updateRule(r.tier, { allow_free_access: v })} testid={`tier-${r.tier}-allow-free`} />
                </td>
                <td className="px-3 py-2 text-center">
                  <SwitchCell value={r.auto_waive_when_earned} onChange={(v) => updateRule(r.tier, { auto_waive_when_earned: v })} testid={`tier-${r.tier}-auto-waive`} />
                </td>
                <td className="px-3 py-2 text-center">
                  <SwitchCell value={r.active} onChange={(v) => updateRule(r.tier, { active: v })} testid={`tier-${r.tier}-active`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[0.7rem] text-lifted-dim">
        These are <strong>defaults only</strong>. Per-affiliate admin overrides always win.
      </p>
    </section>
  );
}

function SwitchCell({ value, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
            className={`relative w-9 h-5 rounded-full transition-colors ${value ? "bg-lifted-green" : "bg-white/15"}`}
            data-testid={testid}>
      <span className={`absolute top-0.5 ${value ? "right-0.5" : "left-0.5"} h-4 w-4 rounded-full bg-white transition-all`} />
    </button>
  );
}

// ---------------- Phase 16 — UpPromote Import tab ---------------------------
function UpPromoteImportTab() {
  const [dryRun, setDryRun]   = useState(true);
  const [busy, setBusy]       = useState(false);
  const [result, setResult]   = useState(null);
  const [reports, setReports] = useState(null);
  const [err, setErr]         = useState("");

  const loadReports = async () => {
    try {
      const { data } = await api.get("/admin/affiliates/import-report");
      setReports(data);
    } catch (e) { /* non-fatal */ }
  };
  React.useEffect(() => { loadReports(); }, []);

  const run = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/admin/affiliates/import-active-uppromote",
                                       { dry_run: dryRun, only_active: true });
      setResult(data);
      if (!dryRun) loadReports();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="uppromote-import-tab">
      <div className="glass-panel p-4">
        <div className="text-xs text-lifted-dim mb-3">
          Pull active affiliates from UpPromote and materialise them as native Lifted
          affiliates. Internal Lifted tracking remains the formal Source of Record
          after import. Run a dry-run first to preview the matches.
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs inline-flex items-center gap-2">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)}
                   data-testid="uppromote-dry-run" />
            Dry run (no writes)
          </label>
          <button onClick={run} disabled={busy}
                  className="btn-lifted text-xs disabled:opacity-50"
                  data-testid="uppromote-import-btn">
            {busy ? "Running…" : dryRun ? "Preview import" : "Import active UpPromote affiliates"}
          </button>
        </div>
        {err && <div className="text-rose-300 text-xs mt-3" data-testid="uppromote-error">{err}</div>}
        {result && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2"
               data-testid="uppromote-result">
            <Stat label="Total rows"        value={result.total_rows} />
            <Stat label="Imported"          value={result.imported}        accent />
            <Stat label="Skipped (existing)" value={result.skipped_existing} />
            <Stat label="Manual review"     value={result.manual_review}   warn />
            <Stat label="Source"            value={result.source} />
          </div>
        )}
      </div>

      <div className="glass-panel p-4" data-testid="uppromote-active-imported">
        <div className="text-xs text-lifted-dim mb-2">Internal active imported profiles</div>
        <div className="font-anton text-2xl text-lifted-green">
          {reports?.active_imported_profiles ?? "—"}
        </div>
      </div>

      <div className="glass-panel p-4" data-testid="uppromote-import-history">
        <div className="text-xs text-lifted-dim mb-2">Recent imports</div>
        {!reports?.reports?.length ? (
          <div className="text-xs text-lifted-dim">No imports yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
              <tr className="border-b border-white/5">
                <th className="px-2 py-2 text-left">When</th>
                <th className="px-2 py-2 text-right">Total</th>
                <th className="px-2 py-2 text-right">Imported</th>
                <th className="px-2 py-2 text-right">Skipped</th>
                <th className="px-2 py-2 text-right">Manual review</th>
                <th className="px-2 py-2 text-left">Source</th>
                <th className="px-2 py-2 text-left">Dry run</th>
              </tr>
            </thead>
            <tbody>
              {reports.reports.map((r) => (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="px-2 py-2">{(r.created_at || "").slice(0, 19)}</td>
                  <td className="px-2 py-2 text-right">{r.total_rows}</td>
                  <td className="px-2 py-2 text-right text-lifted-green">{r.imported}</td>
                  <td className="px-2 py-2 text-right">{r.skipped_existing}</td>
                  <td className="px-2 py-2 text-right text-amber-300">{r.manual_review}</td>
                  <td className="px-2 py-2">{r.source}</td>
                  <td className="px-2 py-2">{r.dry_run ? "yes" : "no"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, warn }) {
  return (
    <div className="glass-panel p-3 border border-white/10">
      <div className="text-[0.6rem] tracking-[0.18em] uppercase font-bebas text-lifted-dim">{label}</div>
      <div className={`font-anton text-2xl ${accent ? "text-lifted-green" : warn ? "text-amber-300" : "text-white"}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

// ---------------- Billing table + editor modal ------------------------------
function BillingTable({ rows, onEdit, showMigration = false }) {
  return (
    <div className="glass-panel overflow-hidden" data-testid="billing-table">
      <table className="w-full text-xs">
        <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
          <tr className="border-b border-white/5">
            <th className="px-3 py-2 text-left">Affiliate</th>
            <th className="px-3 py-2 text-left">Tier</th>
            <th className="px-3 py-2 text-left">Access</th>
            <th className="px-3 py-2 text-left">Pay required</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">{showMigration ? "Migration" : "Next charge"}</th>
            <th className="px-3 py-2 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-white/5" data-testid={`billing-row-${r.id}`}>
              <td className="px-3 py-2">
                <div className="text-white">{r.name || r.email || r.id?.slice(0, 8)}</div>
                <div className="text-[0.6rem] text-lifted-dim">{r.email}</div>
              </td>
              <td className="px-3 py-2 capitalize">{(r.tier || "—").replace("_", " ")}</td>
              <td className="px-3 py-2 capitalize">{(r.access_type || "—").replace("_", " ")}</td>
              <td className="px-3 py-2">
                <span className={`px-1.5 py-0.5 rounded border text-[0.55rem] font-bebas tracking-[0.18em] uppercase ${
                  r.payment_required ? "bg-amber-400/15 text-amber-300 border-amber-400/40"
                                       : "bg-lifted-green/15 text-lifted-green border-lifted-green/40"
                }`}>{r.payment_required ? "Yes" : "No"}</span>
              </td>
              <td className={`px-3 py-2 font-bebas tracking-[0.18em] uppercase ${STATUS_TONE[r.payment_status] || "text-white/60"}`}>
                {r.payment_status}
              </td>
              <td className="px-3 py-2 text-lifted-dim">
                {showMigration ? r.migration_status : (r.next_charge_at || "—")}
              </td>
              <td className="px-3 py-2 text-right">
                <button onClick={() => onEdit(r)}
                        className="btn-ghost text-[0.65rem] inline-flex items-center gap-1"
                        data-testid={`billing-edit-${r.id}`}>
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BillingEditor({ billing, onClose }) {
  const [b, setB] = useState(billing);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/affiliates/${billing.id}/billing-history`);
      setHistory(data.items || []);
    } catch (_) {}  // eslint-disable-line no-unused-vars
  }, [billing.id]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const save = async () => {
    setBusy("save"); setErr("");
    try {
      const patch = {
        payment_required: b.payment_required,
        billing_enabled: b.billing_enabled,
        access_type: b.access_type,
        tier: b.tier,
        monthly_price_cents: b.monthly_price_cents,
        billing_notes: b.billing_notes,
      };
      const { data } = await api.patch(`/admin/affiliates/${billing.id}/billing`, patch);
      setB(data.billing);
      loadHistory();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(""); }
  };

  const action = async (label, endpoint) => {
    setBusy(label); setErr("");
    try {
      const { data } = await api.post(`/admin/affiliates/${billing.id}/${endpoint}`);
      setB(data.billing || b);
      loadHistory();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" onClick={onClose} data-testid="billing-editor">
      <div className="glass-panel max-w-3xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="eyebrow">Billing</div>
            <h2 className="font-anton text-xl uppercase text-white">{b.name || b.email || b.id?.slice(0, 12)}</h2>
            <div className="text-[0.65rem] text-lifted-dim">{b.email}</div>
          </div>
          <button onClick={onClose} className="text-lifted-dim hover:text-white"><X className="h-4 w-4" /></button>
        </div>

        {err && <div className="text-rose-300 text-sm mb-2">{err}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <Field label="Tier">
            <select value={b.tier || "lift_off"} onChange={(e) => setB({ ...b, tier: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs capitalize"
                    data-testid="be-tier">
              {TIERS.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
            </select>
          </Field>
          <Field label="Access type">
            <select value={b.access_type || "paid"} onChange={(e) => setB({ ...b, access_type: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs capitalize"
                    data-testid="be-access-type">
              {ACCESS_TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
            </select>
          </Field>
          <Field label="Payment required">
            <SwitchCell value={!!b.payment_required} onChange={(v) => setB({ ...b, payment_required: v })} testid="be-pay-required" />
          </Field>
          <Field label="Billing enabled">
            <SwitchCell value={!!b.billing_enabled} onChange={(v) => setB({ ...b, billing_enabled: v })} testid="be-billing-enabled" />
          </Field>
          <Field label="Monthly price ($)">
            <input type="number" value={(b.monthly_price_cents || 0) / 100}
                   onChange={(e) => setB({ ...b, monthly_price_cents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                   className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs"
                   data-testid="be-price" />
          </Field>
          <Field label="Stripe subscription">
            <div className="text-[0.7rem] text-lifted-dim font-mono">{b.stripe_subscription_id || "—"}</div>
            <div className="text-[0.65rem] text-lifted-dim">Status: {b.stripe_subscription_status || "—"}</div>
            <div className="text-[0.65rem] text-lifted-dim">Next: {b.next_charge_at || "—"}</div>
          </Field>
          <Field label="Migration status" wide>
            <div className="text-[0.7rem] text-lifted-dim capitalize">{(b.migration_status || "—").replace("_", " ")}</div>
            {b.original_source && <div className="text-[0.65rem] text-lifted-dim">Original source: {b.original_source}</div>}
          </Field>
        </div>

        <div className="mb-4">
          <div className="font-bebas tracking-[0.22em] text-[0.6rem] text-lifted-dim uppercase mb-1">Billing notes</div>
          <textarea value={b.billing_notes || ""} onChange={(e) => setB({ ...b, billing_notes: e.target.value })}
                    rows={2} className="w-full bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs"
                    data-testid="be-notes" placeholder="Any admin notes…" />
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="be-actions">
          <button onClick={save} disabled={busy === "save"} className="btn-lifted text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid="be-save">
            {busy === "save" ? <Loader2 className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3" />} Save
          </button>
          <button onClick={() => action("mark-free", "billing-migration/mark-free")} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="be-mark-free">Mark free</button>
          <button onClick={() => action("mark-paid", "billing-migration/mark-paid")} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="be-mark-paid">Mark paid</button>
          <button onClick={() => action("pause", "billing/pause")} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="be-pause">
            <Pause className="h-3 w-3" /> Pause
          </button>
          <button onClick={() => action("resume", "billing/resume")} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="be-resume">
            <Play className="h-3 w-3" /> Resume
          </button>
          <button onClick={() => action("cancel", "billing/cancel")} className="btn-ghost text-xs inline-flex items-center gap-1 hover:text-rose-300" data-testid="be-cancel">
            Cancel sub
          </button>
          <button onClick={() => action("create-sub", "billing/create-subscription")} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="be-create-sub">
            <CreditCard className="h-3 w-3" /> Create Stripe sub
          </button>
          <button onClick={() => action("sync", "billing/sync-stripe")} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="be-sync">
            <RefreshCw className="h-3 w-3" /> Sync Stripe
          </button>
        </div>

        <details className="border-t border-white/5 pt-3">
          <summary className="cursor-pointer flex items-center gap-2 text-xs text-lifted-dim hover:text-white">
            <History className="h-3 w-3" /> Audit history ({history.length})
          </summary>
          <div className="max-h-60 overflow-y-auto mt-2 space-y-1">
            {history.length === 0 ? (
              <div className="text-[0.7rem] text-lifted-dim italic">No history yet.</div>
            ) : history.map((h) => (
              <div key={h.id} className="text-[0.7rem] border-b border-white/5 pb-1">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-lifted-green">{h.action}</span>
                  <span className="text-lifted-dim">{(h.created_at || "").slice(0, 19).replace("T", " ")}</span>
                </div>
                {h.note && <div className="text-lifted-dim italic">{h.note}</div>}
              </div>
            ))}
          </div>
        </details>
      </div>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <div className="font-bebas tracking-[0.22em] text-[0.6rem] text-lifted-dim uppercase mb-1">{label}</div>
      {children}
    </div>
  );
}
